import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import api from "../../../../api/api";
import BackButton from "../../../../components/BackButton";
import InputField from "../../../../components/InputField";
import SelectField from "../../../../components/SelectField";
import Button from "../../../../components/Button";

import PersonIcon from "@mui/icons-material/Person";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import ContactMailIcon from "@mui/icons-material/ContactMail";

import "../../Common/form.css";
import "../../Admin/MasterData/vendorform.css";

const segmentOptions = [
  {
    value: "FE",
    label: "FE",
  },
  {
    value: "LE",
    label: "LE",
  },
  {
    value: "LI",
    label: "LI",
  },
  {
    value: "FI",
    label: "FI",
  },
];

const vendorKindOptions = [
  {
    value: "Vendor",
    label: "Vendor",
  },
  {
    value: "NVOCC",
    label: "NVOCC",
  },
  {
    value: "Consolidator",
    label: "Consolidator",
  },
  {
    value: "Carrier",
    label: "Carrier",
  },
  {
    value: "CHA",
    label: "CHA",
  },
  {
    value: "Agent",
    label: "Agent",
  },
];

const documentList = [
  {
    name: "cin_certificate",
    label: "CIN Certificate",
    description: "Company incorporation certificate",
  },
  {
    name: "gstn",
    label: "GSTN",
    description: "GST registration certificate",
  },
  {
    name: "pan",
    label: "PAN",
    description: "Company PAN document",
  },
  {
    name: "cancelled_cheque",
    label: "Cancelled Cheque",
    description: "Company bank account cheque",
  },
  {
    name: "company_identification_certificate",
    label: "Company Identification Certificate",
    description: "Company identification document",
  },
];

const initialVendor = {
  company_name: "",
  gst_tan_no: "",
  pan_no: "",
  company_cin_no: "",
  segment: [],
  vendor_kind: "",
  tds_percentage: "",

  flat_house_building: "",
  area_street_sector: "",
  country: "",
  state: "",
  pincode: "",

  poc1_full_name: "",
  poc1_designation: "",
  poc1_email: "",
  poc1_mobile: "",
  poc1_telephone: "",
  poc1_extension: "",

  poc2_full_name: "",
  poc2_designation: "",
  poc2_email: "",
  poc2_mobile: "",
  poc2_telephone: "",
  poc2_extension: "",

  cin_certificate: null,
  gstn: null,
  pan: null,
  cancelled_cheque: null,
  company_identification_certificate: null,
};

const EditVendor = () => {
  const { vendorId, id } = useParams();
  const navigate = useNavigate();

  const currentVendorId = vendorId || id;

  const [vendor, setVendor] = useState(initialVendor);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [gstError, setGstError] = useState("");

  const [documentErrors, setDocumentErrors] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState({});

  // Confirmation modal
  const [showConfirmModal, setShowConfirmModal] =
    useState(false);

  // Success modal
  const [showSuccessModal, setShowSuccessModal] =
    useState(false);

  const [deletedDocuments, setDeletedDocuments] =
    useState([]);

  useEffect(() => {
    if (currentVendorId) {
      fetchVendor();
    } else {
      setLoading(false);
      setError("Vendor ID is missing.");
    }
  }, [currentVendorId]);

  const parseLocation = (location) => {
    if (!location) {
      return {
        flat_house_building: "",
        area_street_sector: "",
        country: "",
        state: "",
        pincode: "",
      };
    }

    const parts = String(location)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    return {
      flat_house_building: parts[0] || "",
      area_street_sector: parts[1] || "",
      country: parts[2] || "",
      state: parts[3] || "",
      pincode: parts[4] || "",
    };
  };

  const normalizeSegments = (segmentType) => {
    if (!segmentType) {
      return [];
    }

    if (Array.isArray(segmentType)) {
      return segmentType
        .map((item) => {
          if (
            typeof item === "object" &&
            item !== null
          ) {
            return item.value || item.label || "";
          }

          return item;
        })
        .filter(Boolean);
    }

    return String(segmentType)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const normalizeVendorKind = (value) => {
    if (!value) {
      return "";
    }

    if (
      typeof value === "object" &&
      value !== null
    ) {
      return (
        value.value ||
        value.label ||
        value.name ||
        ""
      );
    }

    const normalizedValue = String(value).trim();

    const matchedOption =
      vendorKindOptions.find(
        (option) =>
          option.value.toLowerCase() ===
          normalizedValue.toLowerCase(),
      );

    if (matchedOption) {
      return matchedOption.value;
    }

    return normalizedValue;
  };

  const getDocument = (
    documents,
    documentType,
  ) => {
    if (!Array.isArray(documents)) {
      return null;
    }

    return (
      documents.find(
        (document) =>
          document.document_type ===
          documentType,
      ) || null
    );
  };

  const fetchVendor = async () => {
    if (!currentVendorId) {
      setError("Vendor ID is missing.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      setGstError("");

      console.log(
        "Fetching Vendor ID:",
        currentVendorId,
      );

      const response = await api.get(
        `/master-data/vendor-master/${currentVendorId}`,
      );

      console.log(
        "Vendor GET Response:",
        response.data,
      );

      const vendorData =
        response.data?.vendor || {};

      const pocs = Array.isArray(
        response.data?.pocs,
      )
        ? response.data.pocs
        : [];

      const documents = Array.isArray(
        response.data?.documents,
      )
        ? response.data.documents
        : [];

      const authorisedPOC =
        pocs.find(
          (poc) =>
            String(
              poc.poc_type || "",
            ).toUpperCase() ===
            "AUTHORISED",
        ) || {};

      const assignedPOC =
        pocs.find(
          (poc) =>
            String(
              poc.poc_type || "",
            ).toUpperCase() ===
            "ASSIGNED",
        ) || {};

      const segments = normalizeSegments(
        vendorData.segment_type,
      );

      const vendorKind =
        normalizeVendorKind(
          vendorData.vendor_kind,
        );

      const locationData =
        parseLocation(
          vendorData.location,
        );

      setVendor({
        company_name:
          vendorData.name ||
          vendorData.company_name ||
          "",

        gst_tan_no:
          vendorData.gst_number ||
          vendorData.gst_tan_no ||
          "",

        pan_no:
          vendorData.pan_number ||
          vendorData.pan_no ||
          "",

        company_cin_no:
          vendorData.company_cin_no ||
          vendorData.cin_no ||
          "",

        segment: segments,

        vendor_kind: vendorKind,

        tds_percentage:
          vendorData.tds_percentage ?? "",

        flat_house_building:
          vendorData.address_flat ||
          locationData.flat_house_building ||
          "",

        area_street_sector:
          vendorData.address_area ||
          locationData.area_street_sector ||
          "",

        country:
          vendorData.address_country ||
          locationData.country ||
          "",

        state:
          vendorData.address_state ||
          locationData.state ||
          "",

        pincode:
          vendorData.address_pincode ||
          locationData.pincode ||
          "",

        poc1_full_name:
          authorisedPOC.full_name || "",

        poc1_designation:
          authorisedPOC.designation || "",

        poc1_email:
          authorisedPOC.email || "",

        poc1_mobile:
          authorisedPOC.mobile || "",

        poc1_telephone:
          authorisedPOC.telephone || "",

        poc1_extension:
          authorisedPOC.extension || "",

        poc2_full_name:
          assignedPOC.full_name || "",

        poc2_designation:
          assignedPOC.designation || "",

        poc2_email:
          assignedPOC.email || "",

        poc2_mobile:
          assignedPOC.mobile || "",

        poc2_telephone:
          assignedPOC.telephone || "",

        poc2_extension:
          assignedPOC.extension || "",

        cin_certificate: getDocument(
          documents,
          "cin_certificate",
        ),

        gstn: getDocument(
          documents,
          "gstn",
        ),

        pan: getDocument(
          documents,
          "pan",
        ),

        cancelled_cheque: getDocument(
          documents,
          "cancelled_cheque",
        ),

        company_identification_certificate:
          getDocument(
            documents,
            "company_identification_certificate",
          ),
      });

      console.log(
        "Vendor form populated successfully",
      );
    } catch (err) {
      console.error(
        "Failed to fetch vendor:",
        err,
      );

      console.error(
        "GET API Error:",
        err?.response?.data,
      );

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to fetch vendor details.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setVendor((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) {
      setError("");
    }
  };

  const handleGSTChange = (event) => {
    const { value } = event.target;

    setVendor((prev) => ({
      ...prev,
      gst_tan_no: value,
    }));

    if (gstError) {
      setGstError("");
    }

    if (error) {
      setError("");
    }
  };

  const handleSelectChange = (
    name,
    value,
  ) => {
    setVendor((prev) => ({
      ...prev,
      [name]: value || "",
    }));

    if (error) {
      setError("");
    }
  };

  const handleSegmentChange = (
    selected,
  ) => {
    const values = selected
      ? selected.map((item) => item.value)
      : [];

    setVendor((prev) => ({
      ...prev,
      segment: values,
    }));

    if (error) {
      setError("");
    }
  };

  const getSelectedSegments = () => {
    return (vendor.segment || []).map(
      (value) => {
        const option =
          segmentOptions.find(
            (item) =>
              item.value === value,
          );

        return (
          option || {
            value,
            label: value,
          }
        );
      },
    );
  };

  const buildLocation = () => {
    return [
      vendor.flat_house_building,
      vendor.area_street_sector,
      vendor.country,
      vendor.state,
      vendor.pincode,
    ]
      .map((item) =>
        String(item || "").trim(),
      )
      .filter(Boolean)
      .join(", ");
  };

  const getDocumentName = (
    document,
  ) => {
    if (!document) {
      return "";
    }

    if (document instanceof File) {
      return document.name;
    }

    return (
      document.original_name ||
      document.file_name ||
      document.name ||
      document.document_name ||
      "Existing document"
    );
  };

  const getDocumentUrl = (
    document,
  ) => {
    if (!document) {
      return "";
    }

    return (
      document.file_url ||
      document.document_url ||
      document.url ||
      document.path ||
      document.file_path ||
      ""
    );
  };

  

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!currentVendorId) {
      setError("Vendor ID is missing.");
      return;
    }

    if (
      !vendor.company_name ||
      !vendor.company_name.trim()
    ) {
      setError("Vendor name is required.");
      return;
    }

    setError("");
    setGstError("");

    
    setShowConfirmModal(true);
  };

 

  const handleConfirmUpdate =
    async () => {
      if (!currentVendorId) {
        setError("Vendor ID is missing.");
        return;
      }

      try {
        setSaving(true);

        setError("");
        setGstError("");

        const formData = new FormData();

        formData.append(
          "name",
          vendor.company_name.trim(),
        );

        formData.append(
          "pan_number",
          vendor.pan_no || "",
        );

        formData.append(
          "gst_number",
          vendor.gst_tan_no || "",
        );

        formData.append(
          "tds_percentage",
          vendor.tds_percentage ?? "",
        );

        formData.append(
          "segment_type",
          Array.isArray(vendor.segment)
            ? vendor.segment.join(",")
            : vendor.segment || "",
        );

        formData.append(
          "vendor_kind",
          vendor.vendor_kind || "",
        );

        formData.append(
          "company_cin_no",
          vendor.company_cin_no || "",
        );

        formData.append(
          "address_flat",
          vendor.flat_house_building ||
            "",
        );

        formData.append(
          "address_area",
          vendor.area_street_sector ||
            "",
        );

        formData.append(
          "address_country",
          vendor.country || "",
        );

        formData.append(
          "address_state",
          vendor.state || "",
        );

        formData.append(
          "address_pincode",
          vendor.pincode || "",
        );

        formData.append(
          "location",
          buildLocation(),
        );

        const poc1 = {
          full_name:
            vendor.poc1_full_name || "",

          designation:
            vendor.poc1_designation || "",

          email:
            vendor.poc1_email || "",

          mobile:
            vendor.poc1_mobile || "",

          telephone:
            vendor.poc1_telephone || "",

          extension:
            vendor.poc1_extension || "",
        };

        formData.append(
          "poc1",
          JSON.stringify(poc1),
        );

        const poc2 = {
          full_name:
            vendor.poc2_full_name || "",

          designation:
            vendor.poc2_designation || "",

          email:
            vendor.poc2_email || "",

          mobile:
            vendor.poc2_mobile || "",

          telephone:
            vendor.poc2_telephone || "",

          extension:
            vendor.poc2_extension || "",
        };

        formData.append(
          "poc2",
          JSON.stringify(poc2),
        );

        documentList.forEach((doc) => {
          const selectedDocument =
            vendor[doc.name];

          if (
            selectedDocument instanceof File
          ) {
            formData.append(
              "documents",
              selectedDocument,
            );
          }
        });

        if (
          deletedDocuments.length > 0
        ) {
          formData.append(
            "deleted_document_ids",
            JSON.stringify(
              deletedDocuments,
            ),
          );
        }

        console.log(
          "=================================",
        );

        console.log(
          "Updating Vendor ID:",
          currentVendorId,
        );

        console.log(
          "=================================",
        );

        for (
          const [key, value] of formData.entries()
        ) {
          console.log(
            key,
            value instanceof File
              ? `FILE: ${value.name}`
              : value,
          );
        }

        const response = await api.put(
          `/master-data/vendor-master/${currentVendorId}`,
          formData,
        );

        console.log(
          "Vendor updated successfully",
        );

        console.log(
          "PUT Response:",
          response.data,
        );

        // Confirmation modal close
        setShowConfirmModal(false);

        // Success modal open
        setShowSuccessModal(true);
      } catch (err) {
        console.error(
          "Error updating vendor:",
          err,
        );

        console.error(
          "Update API Error:",
          err?.response?.data,
        );

        const apiMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "";

        const normalizedMessage =
          String(
            apiMessage,
          ).toLowerCase();

        const isDuplicateGST =
          normalizedMessage.includes(
            "gst",
          ) &&
          normalizedMessage.includes(
            "already exists",
          );

        if (isDuplicateGST) {
          setGstError(
            "A vendor with this GST number already exists",
          );

          setError("");
        } else {
          setError(
            apiMessage ||
              "Failed to update vendor. Please try again.",
          );

          setGstError("");
        }

        // Error hone par confirmation modal close
        setShowConfirmModal(false);
      } finally {
        setSaving(false);
      }
    };

  

  const handleConfirmCancel = () => {
    if (saving) {
      return;
    }

    setShowConfirmModal(false);
  };

  // =====================================================
  // DOCUMENT UPLOAD
  // =====================================================

  const handleDocumentUpload = (
    event,
    documentName,
  ) => {
    const file =
      event.currentTarget.files?.[0];

    if (!file) {
      return;
    }

    setDocumentErrors((prev) => ({
      ...prev,
      [documentName]: "",
    }));

    setUploadSuccess((prev) => ({
      ...prev,
      [documentName]: false,
    }));

    const isPdf =
      file.type ===
        "application/pdf" ||
      file.name
        .toLowerCase()
        .endsWith(".pdf");

    if (!isPdf) {
      setDocumentErrors((prev) => ({
        ...prev,
        [documentName]:
          "Only PDF files are supported.",
      }));

      setVendor((prev) => ({
        ...prev,
        [documentName]: null,
      }));

      event.currentTarget.value = "";

      return;
    }

    const maxSize =
      5 * 1024 * 1024;

    if (file.size > maxSize) {
      setDocumentErrors((prev) => ({
        ...prev,
        [documentName]:
          "PDF file size should not exceed 5 MB.",
      }));

      setVendor((prev) => ({
        ...prev,
        [documentName]: null,
      }));

      event.currentTarget.value = "";

      return;
    }

    setVendor((prev) => ({
      ...prev,
      [documentName]: file,
    }));

    setDocumentErrors((prev) => ({
      ...prev,
      [documentName]: "",
    }));

    setUploadSuccess((prev) => ({
      ...prev,
      [documentName]: true,
    }));

    setTimeout(() => {
      setUploadSuccess((prev) => ({
        ...prev,
        [documentName]: false,
      }));
    }, 1000);

    console.log(
      "PDF selected:",
      {
        documentName,
        name: file.name,
        type: file.type,
        size: file.size,
      },
    );

    event.currentTarget.value = "";
  };



  const handleViewDocument = (
    document,
  ) => {
    if (!document) {
      return;
    }

    try {
      if (document instanceof File) {
        const fileUrl =
          URL.createObjectURL(
            document,
          );

        window.open(
          fileUrl,
          "_blank",
          "noopener,noreferrer",
        );

        setTimeout(() => {
          URL.revokeObjectURL(
            fileUrl,
          );
        }, 60000);

        return;
      }

      const documentUrl =
        getDocumentUrl(document);

      if (!documentUrl) {
        setError(
          "Document preview URL is not available.",
        );

        return;
      }

      window.open(
        documentUrl,
        "_blank",
        "noopener,noreferrer",
      );
    } catch (err) {
      console.error(
        "Unable to preview PDF:",
        err,
      );

      setError(
        "Unable to preview this PDF.",
      );
    }
  };



  const handleRemoveDocument = (
    documentName,
  ) => {
    const existingDocument =
      vendor[documentName];

    if (
      existingDocument &&
      !(
        existingDocument instanceof
        File
      )
    ) {
      const documentId =
        existingDocument.id ||
        existingDocument.document_id;

      if (documentId) {
        setDeletedDocuments(
          (prev) => {
            if (
              prev.includes(
                documentId,
              )
            ) {
              return prev;
            }

            return [
              ...prev,
              documentId,
            ];
          },
        );
      }
    }

    setVendor((prev) => ({
      ...prev,
      [documentName]: null,
    }));

    setDocumentErrors((prev) => ({
      ...prev,
      [documentName]: "",
    }));

    setUploadSuccess((prev) => ({
      ...prev,
      [documentName]: false,
    }));

    setError("");
  };



  const handleSuccessModalOk =
    () => {
      setShowSuccessModal(false);

      navigate(
        "/master-data/vendor-master",
      );
    };

  

  if (loading) {
    return (
      <>
        <BackButton />

        <div className="text-center py-5">
          Loading vendor details...
        </div>
      </>
    );
  }

  return (
    <>
      <BackButton />

      <div className="vendor-master-page">
        {error && (
          <small className="text-danger d-block mt-1">
            {error}
          </small>
        )}

        <form
          onSubmit={handleSubmit}
          noValidate
        >
       
          <fieldset className="vendor-section mt-4">
            <legend className="vendor-section-legend">
              <span className="vendor-section-icon">
                <PersonIcon fontSize="small" />
              </span>

              <span className="vendor-section-title">
                Company Details
              </span>
            </legend>

            <p className="vendor-section-description">
              Edit company registration and
              address information.
            </p>

            <div className="vendor-section-body">
              <div className="row g-3 mb-3">
                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Company Name"
                    name="company_name"
                    value={
                      vendor.company_name
                    }
                    onChange={handleChange}
                    placeholder="Enter company name"
                    required
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="GST/TAN No."
                    name="gst_tan_no"
                    value={
                      vendor.gst_tan_no
                    }
                    onChange={
                      handleGSTChange
                    }
                    placeholder="Enter GST/TAN number"
                    required
                  />

                  {gstError && (
                    <small
                      className="text-danger d-block mt-1"
                      role="alert"
                    >
                      {gstError}
                    </small>
                  )}
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="PAN No."
                    name="pan_no"
                    value={vendor.pan_no}
                    onChange={handleChange}
                    placeholder="Enter PAN number"
                    required
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <SelectField
                    label="Vendor Kind"
                    name="vendor_kind"
                    value={
                      vendor.vendor_kind
                    }
                    options={
                      vendorKindOptions
                    }
                    onChange={(value) =>
                      handleSelectChange(
                        "vendor_kind",
                        value,
                      )
                    }
                    placeholder="Select vendor kind"
                    required
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <label className="form-label mt-1">
                    Segments
                  </label>

                  <Select
                    options={
                      segmentOptions
                    }
                    value={getSelectedSegments()}
                    onChange={
                      handleSegmentChange
                    }
                    isMulti
                    isSearchable
                    placeholder="Select segments"
                    classNamePrefix="vendor-select"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Company CIN No."
                    name="company_cin_no"
                    value={
                      vendor.company_cin_no
                    }
                    onChange={handleChange}
                    placeholder="Enter CIN number"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="TDS (%)"
                    name="tds_percentage"
                    type="number"
                    value={
                      vendor.tds_percentage
                    }
                    onChange={handleChange}
                    placeholder="Enter TDS percentage"
                  />
                </div>
              </div>

              {/* ADDRESS */}

              <div className="vendor-subsection">
                <div className="vendor-subsection-title">
                  Registered Address
                </div>

                <div className="row g-3">
                  <div className="col-12 col-lg-6">
                    <InputField
                      label="Flat/House No./Building/Company/Apartment"
                      name="flat_house_building"
                      value={
                        vendor.flat_house_building
                      }
                      onChange={handleChange}
                      placeholder="Enter address"
                    />
                  </div>

                  <div className="col-12 col-lg-6">
                    <InputField
                      label="Area/Street/Sector/Town/Village"
                      name="area_street_sector"
                      value={
                        vendor.area_street_sector
                      }
                      onChange={handleChange}
                      placeholder="Enter area/street/sector"
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <InputField
                      label="Country"
                      name="country"
                      value={
                        vendor.country
                      }
                      onChange={handleChange}
                      placeholder="Enter country"
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <InputField
                      label="State"
                      name="state"
                      value={vendor.state}
                      onChange={handleChange}
                      placeholder="Enter state"
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <InputField
                      label="Pincode"
                      name="pincode"
                      value={
                        vendor.pincode
                      }
                      onChange={handleChange}
                      placeholder="Enter pincode"
                    />
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

         

          <fieldset className="vendor-section mt-4">
            <legend className="vendor-section-legend">
              <span className="vendor-section-icon">
                <ContactMailIcon fontSize="small" />
              </span>

              <span className="vendor-section-title">
                Contact Details
              </span>
            </legend>

            <p className="vendor-section-description">
              Edit authorised and assigned
              points of contact.
            </p>

            <div className="vendor-section-body">
              {/* POC 1 */}

              <div className="vendor-poc-header">
                <div className="vendor-poc-heading">
                  <span className="vendor-poc-label">
                    POC - 1
                  </span>

                  <span className="vendor-poc-badge authorised">
                    Authorised
                  </span>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Full Name"
                    name="poc1_full_name"
                    value={
                      vendor.poc1_full_name
                    }
                    onChange={handleChange}
                    placeholder="Enter full name"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Designation"
                    name="poc1_designation"
                    value={
                      vendor.poc1_designation
                    }
                    onChange={handleChange}
                    placeholder="Enter designation"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mail ID"
                    name="poc1_email"
                    type="email"
                    value={
                      vendor.poc1_email
                    }
                    onChange={handleChange}
                    placeholder="Enter email address"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mobile No."
                    name="poc1_mobile"
                    type="tel"
                    value={
                      vendor.poc1_mobile
                    }
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Telephone No."
                    name="poc1_telephone"
                    type="tel"
                    value={
                      vendor.poc1_telephone
                    }
                    onChange={handleChange}
                    placeholder="Enter telephone number"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Extension No."
                    name="poc1_extension"
                    value={
                      vendor.poc1_extension
                    }
                    onChange={handleChange}
                    placeholder="Enter extension number"
                  />
                </div>
              </div>

              {/* POC 2 */}

              <div className="vendor-poc-header">
                <div className="vendor-poc-heading">
                  <span className="vendor-poc-label">
                    POC - 2
                  </span>

                  <span className="vendor-poc-badge assigned">
                    Assigned
                  </span>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Full Name"
                    name="poc2_full_name"
                    value={
                      vendor.poc2_full_name
                    }
                    onChange={handleChange}
                    placeholder="Enter full name"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Designation"
                    name="poc2_designation"
                    value={
                      vendor.poc2_designation
                    }
                    onChange={handleChange}
                    placeholder="Enter designation"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mail ID"
                    name="poc2_email"
                    type="email"
                    value={
                      vendor.poc2_email
                    }
                    onChange={handleChange}
                    placeholder="Enter email address"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Mobile No."
                    name="poc2_mobile"
                    type="tel"
                    value={
                      vendor.poc2_mobile
                    }
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Telephone No."
                    name="poc2_telephone"
                    type="tel"
                    value={
                      vendor.poc2_telephone
                    }
                    onChange={handleChange}
                    placeholder="Enter telephone number"
                  />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <InputField
                    label="Extension No."
                    name="poc2_extension"
                    value={
                      vendor.poc2_extension
                    }
                    onChange={handleChange}
                    placeholder="Enter extension number"
                  />
                </div>
              </div>
            </div>
          </fieldset>

       

          <fieldset className="vendor-section mt-4">
            <legend className="vendor-section-legend">
              <span className="vendor-section-icon">
                <ContactSupportIcon fontSize="small" />
              </span>

              <span className="vendor-section-title">
                Supporting Docs Checklist
              </span>
            </legend>

            <p className="vendor-section-description">
              Upload or replace the required
              documents in PDF format.
            </p>

            <div className="vendor-section-body">
              <div className="row g-3 mb-3">
                {documentList.map(
                  (doc) => {
                    const uploadedFile =
                      vendor[doc.name];

                    const documentError =
                      documentErrors[
                        doc.name
                      ];

                    const fileName =
                      getDocumentName(
                        uploadedFile,
                      );

                    return (
                      <div
                        className="col-12 col-md-6 col-lg-4"
                        key={doc.name}
                      >
                        <div
                          className={`vendor-document-upload-card ${
                            uploadedFile
                              ? "uploaded"
                              : ""
                          } ${
                            documentError
                              ? "has-error"
                              : ""
                          }`}
                        >
                          <div className="vendor-document-info">
                            <div
                              className={`vendor-document-icon ${
                                uploadedFile
                                  ? "success"
                                  : documentError
                                    ? "error"
                                    : ""
                              }`}
                            >
                              {uploadedFile
                                ? "✓"
                                : "PDF"}
                            </div>

                            <div className="vendor-document-title">
                              <span>
                                {
                                  doc.label
                                }
                              </span>

                              <small>
                                {
                                  doc.description
                                }
                              </small>
                            </div>
                          </div>

                          <input
                            type="file"
                            id={`document-${doc.name}`}
                            accept="application/pdf,.pdf"
                            style={{
                              display:
                                "none",
                            }}
                            onChange={(
                              event,
                            ) =>
                              handleDocumentUpload(
                                event,
                                doc.name,
                              )
                            }
                          />

                          {documentError && (
                            <div
                              className="vendor-document-error"
                              role="alert"
                            >
                              {
                                documentError
                              }
                            </div>
                          )}

                          {uploadSuccess[
                            doc.name
                          ] && (
                            <div className="vendor-uploaded-status">
                              <span>
                                ✓
                              </span>
                              PDF uploaded
                              successfully
                            </div>
                          )}

                          <div className="vendor-document-actions">
                            {!uploadedFile ? (
                              <label
                                htmlFor={`document-${doc.name}`}
                                className="vendor-upload-btn"
                              >
                                Upload PDF
                              </label>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="vendor-view-btn"
                                  onClick={() =>
                                    handleViewDocument(
                                      uploadedFile,
                                    )
                                  }
                                >
                                  View
                                </button>

                                <label
                                  htmlFor={`document-${doc.name}`}
                                  className="vendor-replace-btn"
                                >
                                  Replace
                                </label>

                                <button
                                  type="button"
                                  className="vendor-remove-btn"
                                  onClick={() =>
                                    handleRemoveDocument(
                                      doc.name,
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              </>
                            )}
                          </div>

                          {uploadedFile && (
                            <div
                              className="vendor-file-name"
                              title={
                                fileName
                              }
                            >
                              📄{" "}
                              {
                                fileName
                              }
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </fieldset>

       

          <div className="d-flex align-items-center justify-content-end gap-2 py-3 me-3">
            <Button
              type="submit"
              variant="primary"
              text="Save Changes"
              loading={saving}
            />
          </div>
        </form>
      </div>

     

      {showConfirmModal && (
        <div
          className="vendor-confirm-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vendor-confirm-title"
        >
          <div className="vendor-confirm-modal">
            {/* HEADER */}

            <div className="vendor-confirm-modal-header">
              <h4 id="vendor-confirm-title">
                Confirm Submission
              </h4>

              <button
                type="button"
                className="vendor-confirm-close"
                onClick={
                  handleConfirmCancel
                }
                disabled={saving}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* BODY */}

            <div className="vendor-confirm-modal-body">
              <p>
                Are you sure you want to
                submit this ?
              </p>
            </div>

            {/* FOOTER */}

            <div className="vendor-confirm-modal-footer">
              <button
                type="button"
                className="vendor-confirm-cancel-btn"
                onClick={
                  handleConfirmCancel
                }
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="button"
                className="vendor-confirm-confirm-btn"
                onClick={
                  handleConfirmUpdate
                }
                disabled={saving}
              >
                {saving
                  ? "Updating..."
                  : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSuccessModal && (
        <div
          className="vendor-success-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vendor-success-title"
        >
          <div className="vendor-success-modal">
          

            <h4 id="vendor-success-title">
              Vendor Updated Successfully
            </h4>

            <p>
              Vendor details have been
              updated successfully.
            </p>

            <button
              type="button"
              className="btn btn-primary"
              onClick={
                handleSuccessModalOk
              }
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EditVendor;